import { useId, useState, type ComponentType } from "react";
import { NavLink, Outlet, useLoaderData, useLocation, useMatches, useNavigation, useParams, type LoaderFunctionArgs } from "react-router";
import { SetupGate } from "../../components/setup-gate.jsx";
import { Deferred } from "../../components/states.jsx";
import { RouteSkeleton } from "../../components/skeletons.jsx";
import { WorkspaceSwitcher } from "../../components/workspace-switcher.jsx";
import { useI18n, type LocaleCode } from "../../i18n/index.js";
import { authClient } from "../../lib/auth-client.js";
import { apiContext, sessionContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { usePreferences, useStoredPreferences } from "../../lib/preferences.js";
import { isDone, loadProgress, type Progress } from "./get-started.jsx";
import type { ThemeMode } from "../../theme/index.js";
import {
  ChangeIcon,
  ChevronLeftIcon,
  ChevronUpDownIcon,
  ConnectorIcon,
  cx,
  DecisionIcon,
  DraftIcon,
  GetStartedIcon,
  HealthIcon,
  InboxIcon,
  KeyIcon,
  LanguageIcon,
  LogOutIcon,
  Menu,
  MonitorIcon,
  MoonIcon,
  PreferencesIcon,
  ProfileIcon,
  RepositoryIcon,
  ReviewIcon,
  SettingsIcon,
  Skeleton,
  SunIcon,
  TerminalIcon,
  UsersIcon,
  WorkspaceIcon,
} from "../../ui/index.js";

/**
 * Sidebar link that reacts to the click, not to the navigation finishing.
 * In data mode `isActive` only flips once every loader has resolved, so a plain
 * NavLink leaves the sidebar looking untouched for a whole round trip;
 * `isPending` marks the link the router is on its way to.
 */
function NavItem({ to, end, icon, label }: { to: string; end?: boolean; icon: React.ReactNode; label: string }) {
  return (
    <NavLink to={to} end={end ?? false} className={({ isActive, isPending }) => cx("dt-nav__link", isActive && "active", isPending && "pending")}>
      {icon}
      {label}
    </NavLink>
  );
}

/**
 * Page layouts: the only widths a page in the workspace shell may take, centered in the main area.
 * `full` has no limit, `wide` is for lists and dashboards, `normal` for one record or a form.
 * A route declares its layout as `handle`; the header and the content always share it. A child
 * without a layout takes its parent's. `skeleton` is the page drawn while a fresh load reads the session.
 * `gated` pages wait for the workspace to be set up (D-0022) and say so meanwhile.
 */
export type PageLayout = "full" | "wide" | "normal";
export type PageHandle = { layout?: PageLayout; skeleton?: ComponentType; gated?: boolean };

function usePageLayout(): PageLayout {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const layout = (matches[i]!.handle as PageHandle | undefined)?.layout;
    if (layout) return layout;
  }
  return "wide";
}

function useGated(): boolean {
  const matches = useMatches();
  return (matches.at(-1)?.handle as PageHandle | undefined)?.gated === true;
}

export function workspaceLayoutLoader({ context }: LoaderFunctionArgs) {
  const workspace = context.get(workspaceContext);
  const api = context.get(apiContext).workspace(workspace.id);
  return { me: context.get(sessionContext)!, workspace, api, progress: loadProgress(workspace, api) };
}

function UserMenu({ email, name, image }: { email?: string | undefined; name?: string | undefined; image?: string | null | undefined }) {
  const { t, languages } = useI18n();
  const preferences = usePreferences();
  const initial = (name || email || "?").slice(0, 1);
  return (
    <Menu.Root>
      <Menu.Trigger className="dt-user" aria-label={t("user.menuAria")}>
        <span className="dt-user__avatar" aria-hidden="true">
          {image ? <img src={image} alt="" /> : initial}
        </span>
        <span className="dt-user__text">
          <span className="dt-user__name">{name || t("user.signedIn")}</span>
          <span className="dt-user__email">{email}</span>
        </span>
        <ChevronUpDownIcon className="dt-user__chevron" />
      </Menu.Trigger>
      <Menu.Content align="start" side="top" style={{ minWidth: "var(--anchor-width)" }}>
        <Menu.RadioGroup value={preferences.appearance} onValueChange={(v) => preferences.setAppearance(v as ThemeMode)}>
          <Menu.GroupLabel>{t("appearance.label")}</Menu.GroupLabel>
          <Menu.RadioItem value="system" closeOnClick={false}>
            <span className="dt-inline dt-gap-2 dt-align-center">
              <MonitorIcon /> {t("appearance.system")}
            </span>
          </Menu.RadioItem>
          <Menu.RadioItem value="light" closeOnClick={false}>
            <span className="dt-inline dt-gap-2 dt-align-center">
              <SunIcon /> {t("appearance.light")}
            </span>
          </Menu.RadioItem>
          <Menu.RadioItem value="dark" closeOnClick={false}>
            <span className="dt-inline dt-gap-2 dt-align-center">
              <MoonIcon /> {t("appearance.dark")}
            </span>
          </Menu.RadioItem>
        </Menu.RadioGroup>
        <Menu.Separator />
        {/* Every language the lang-router ships shows up here; adding one needs no change to this file. */}
        <Menu.RadioGroup value={preferences.locale} onValueChange={(v) => preferences.setLanguage(v as LocaleCode)}>
          <Menu.GroupLabel>{t("language.label")}</Menu.GroupLabel>
          {languages.map((language) => (
            <Menu.RadioItem key={language.code} value={language.code} closeOnClick={false}>
              <span className="dt-inline dt-gap-2 dt-align-center" lang={language.tag}>
                <LanguageIcon /> {language.nativeLabel}
              </span>
            </Menu.RadioItem>
          ))}
        </Menu.RadioGroup>
        <Menu.Separator />
        <Menu.Item icon={<LogOutIcon />} onClick={() => authClient.signOut().then(() => location.assign("/sign-in"))}>
          {t("user.signOut")}
        </Menu.Item>
      </Menu.Content>
    </Menu.Root>
  );
}

/**
 * Also the route's HydrateFallback: on a fresh load it renders before the session is read, with no
 * loader data yet, as the shell around the page's skeleton. Being the same component both times, the
 * shell stays mounted when the data arrives — the navigation a person may already be clicking stays put.
 */
export function WorkspaceLayout() {
  const data = useLoaderData<typeof workspaceLayoutLoader>() as ReturnType<typeof workspaceLayoutLoader> | undefined;
  const { workspaceSlug = "" } = useParams();
  const location = useLocation();
  useStoredPreferences(data?.me.actor);
  const gated = useGated();
  // Both branches render Shell itself, so React keeps it (and its navigation) when the data arrives.
  if (!data) {
    return (
      <Shell base={`/w/${workspaceSlug}`} switcher={<SwitcherSkeleton />} user={<UserSkeleton />}>
        <RouteSkeleton />
      </Shell>
    );
  }
  const { me, workspace, api, progress } = data;
  return (
    <Shell
      base={`/w/${workspace.slug}`}
      progress={progress}
      switcher={<WorkspaceSwitcher workspaces={me.workspaces} current={workspace} />}
      user={<UserMenu email={me.actor.email} name={me.actor.name} image={me.actor.image} />}
    >
      {/* Keyed by path, so opening another record of the same kind (D-0001 → D-0002, another workspace's
          page) is a new page that shows its skeleton at once, not the old one held until the new one loads. */}
      {gated && workspace.setup ? <SetupGate workspace={{ ...workspace, setup: workspace.setup }} api={api} /> : <Outlet key={location.pathname} />}
    </Shell>
  );
}

/** The shell before the session is known: the navigation is real, who and where are placeholders. */
export function ShellSkeleton({ slug, children }: { slug: string; children: React.ReactNode }) {
  return (
    <Shell base={`/w/${slug}`} switcher={<SwitcherSkeleton />} user={<UserSkeleton />}>
      {children}
    </Shell>
  );
}

function SwitcherSkeleton() {
  return (
    <div className="dt-ws-switcher" aria-hidden="true">
      <Skeleton shape="block" width={22} height={22} />
      <span className="dt-ws-switcher__name">
        <Skeleton width="60%" />
      </span>
    </div>
  );
}

function UserSkeleton() {
  return (
    <div className="dt-user" aria-hidden="true">
      <Skeleton shape="circle" width={28} height={28} />
      <span className="dt-user__text">
        <span className="dt-user__name">
          <Skeleton width="50%" />
        </span>
        <span className="dt-user__email">
          <Skeleton width="80%" />
        </span>
      </span>
    </div>
  );
}

function Shell({ base, switcher, user, progress, children }: { base: string; switcher: React.ReactNode; user: React.ReactNode; progress?: Promise<Progress>; children: React.ReactNode }) {
  const { t } = useI18n();
  const layout = usePageLayout();
  const personalId = useId();
  const workspaceGroupId = useId();
  const settings = `${base}/settings`;
  const isSettings = (pathname: string) => pathname === settings || pathname.startsWith(`${settings}/`);
  const location = useLocation();
  const navigation = useNavigation();
  // Like NavItem's pending state, the sidebar follows the click, not the loaders.
  const inSettings = isSettings((navigation.location ?? location).pathname);
  // "Back" returns to the last page outside Settings; opened directly, the workspace's first page.
  const [backTo, setBackTo] = useState(base);
  const here = location.pathname + location.search;
  if (!isSettings(location.pathname) && backTo !== here) setBackTo(here);
  return (
    <div className="dt-shell">
      <aside className="dt-sidebar">
        {/*
          The sidebar is two pages side by side: the workspace one, and the settings
          one that slides in for it — and nothing else — while a settings page is open.
          The page out of view is inert, so neither focus nor a screen reader lands on it.
        */}
        <div className="dt-sidebar__slide t-page-slide" data-page={inSettings ? "2" : "1"}>
          <div className="dt-sidebar__page t-page" data-page-id="1" inert={inSettings}>
            {switcher}
            {/*
              Every item is something you do. Component and Constraint are absent on
              purpose: they have detail pages but no list page, because nobody opens
              a page to browse them — they are met inside a finding or a cascade, at
              the moment they matter.
            */}
            <nav className="dt-nav" aria-label={t("nav.aria")}>
              {/* Only until the workspace is through it: after that it is a page nobody needs. */}
              {progress && (
                <Deferred data={progress} fallback={null}>
                  {(p) =>
                    isDone(p) ? null : (
                      <div className="dt-nav__group">
                        <NavItem to={`${base}/get-started`} icon={<GetStartedIcon />} label={t("nav.getStarted")} />
                      </div>
                    )
                  }
                </Deferred>
              )}
              <div className="dt-nav__group">
                <NavItem to={`${base}/inbox`} icon={<InboxIcon />} label={t("nav.inbox")} />
                <NavItem to={`${base}/reviews`} icon={<ReviewIcon />} label={t("nav.reviews")} />
                <NavItem to={`${base}/drafts`} icon={<DraftIcon />} label={t("nav.drafts")} />
                <NavItem to={`${base}/my-changes`} icon={<ChangeIcon />} label={t("nav.myChanges")} />
              </div>
              <div className="dt-nav__group">
                <NavItem to={`${base}/decisions`} icon={<DecisionIcon />} label={t("nav.decisions")} />
                <NavItem to={`${base}/health`} icon={<HealthIcon />} label={t("nav.health")} />
              </div>
              <div className="dt-nav__group dt-nav__group--end">
                <NavItem to={settings} icon={<SettingsIcon />} label={t("nav.settings")} />
              </div>
            </nav>
            <div className="dt-sidebar__footer">{user}</div>
          </div>
          <div className="dt-sidebar__page t-page" data-page-id="2" inert={!inSettings}>
            <nav className="dt-nav" aria-label={t("settings.title")}>
              <div className="dt-nav__group">
                {/* `end`: Back is never the page you are on. */}
                <NavItem to={backTo} end icon={<ChevronLeftIcon weight="regular" />} label={t("common.back")} />
              </div>
              <div className="dt-nav__group" role="group" aria-labelledby={personalId}>
                <div id={personalId} className="dt-nav__section">
                  {t("settings.group.personal")}
                </div>
                <NavItem to={settings} end icon={<PreferencesIcon />} label={t("settings.preferences.title")} />
                <NavItem to={`${settings}/profile`} icon={<ProfileIcon />} label={t("settings.profile.title")} />
                <NavItem to={`${settings}/agents`} icon={<TerminalIcon />} label={t("agents.title")} />
              </div>
              <div className="dt-nav__group" role="group" aria-labelledby={workspaceGroupId}>
                <div id={workspaceGroupId} className="dt-nav__section">
                  {t("settings.group.workspace")}
                </div>
                <NavItem to={`${settings}/general`} icon={<WorkspaceIcon />} label={t("settings.workspace.title")} />
                <NavItem to={`${settings}/approval-rules`} icon={<ReviewIcon />} label={t("settings.approval.title")} />
                <NavItem to={`${settings}/repositories`} icon={<RepositoryIcon />} label={t("settings.repositories.title")} />
                <NavItem to={`${settings}/members`} icon={<UsersIcon />} label={t("members.title")} />
                <NavItem to={`${settings}/api-keys`} icon={<KeyIcon />} label={t("apiKeys.title")} />
                <NavItem to={`${settings}/connectors`} icon={<ConnectorIcon />} label={t("connectors.title")} />
              </div>
            </nav>
          </div>
        </div>
      </aside>
      <main className="dt-main">
        <div className={cx("dt-main__inner", `dt-layout--${layout}`)}>{children}</div>
      </main>
    </div>
  );
}
